import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getToken, setToken } from './token-store';

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
};

const AuthContext = createContext<AuthState | null>(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const authUrl = import.meta.env.VITE_AUTH_URL;
    const hasTokenInHash = location.hash.startsWith('#token=');

    // Case 1: Has token in hash - use the new token (fresh login) and clean up URL
    if (hasTokenInHash) {
      const newToken = location.hash.substring(7); // Remove '#token='
      setToken(newToken);
      window.history.replaceState(null, '', location.pathname + location.search);
      setState({ isAuthenticated: true, isLoading: false, error: null });

      return;
    }

    // Case 2: Already authenticated with an in-memory token (same tab, not reloaded)
    if (getToken()) {
      setState({ isAuthenticated: true, isLoading: false, error: null });

      return;
    }

    // Case 3: No token - fetch or redirect based on environment
    if (authUrl) {
      // Dev: fetch token directly from endpoint (returns JSON)
      if (import.meta.env.DEV) {
        fetch(authUrl)
          .then((res) => res.json())
          .then((data: { token: string }) => {
            setToken(data.token);
            setState({ isAuthenticated: true, isLoading: false, error: null });
          })
          .catch((err) => {
            setState({ isAuthenticated: false, isLoading: false, error: String(err) });
          });

        return;
      }

      // Prod: redirect to auth URL (will come back with #token=)
      window.location.href = `${authUrl}?return=${encodeURIComponent(window.location.href)}`;

      return;
    }

    // Case 4: No auth URL configured - show error
    setState({
      isAuthenticated: false,
      isLoading: false,
      error: 'Authentication is not configured. Please contact your administrator.',
    });
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export { useAuth, AuthProvider };
